// Editorial archive data for We Cook Art — Pavitra Mishra
// Voice blends refined editorial language with the PDF's "recipe / kitchen" metaphor.

export const PROJECTS = [
  {
    slug: "sun-moon",
    index: "Recipe 01",
    title: "Sun / Moon",
    year: "2024",
    medium: "Conceptual Shoot",
    concept:
      "A visual exploration of duality held within a single body — sun and moon, gold and silver, heat and stillness, chaos and calm.",
    statement:
      "Two opposing energies are asked to share one frame. Light is never allowed to win completely; neither is shadow. What remains is the quiet negotiation between them — a face caught between two skies.",
    process: [
      "Styling drawn from mythology, Indian jewellery and celestial symbolism.",
      "Lighting built to hold gold and silver in the same breath, refusing a single dominant tone.",
      "Frames composed around contrast — heat against softness, ornament against bare skin.",
    ],
    credits: [
      { role: "Concept, Creative Direction, Styling & Modeling", name: "Pavitra Mishra" },
      { role: "Photography & Cinematography", name: "Aanchal Chawla, Deep Vakil, Sapan Shah, Aahna" },
      { role: "Hair & Makeup", name: "Aangi Shah" },
      { role: "Location", name: "FTV, Ahmedabad" },
    ],
    type: "image",
    cover: "/images/sunmoon-03.jpg",
    gallery: [
      "/images/sunmoon-01.jpg",
      "/images/sunmoon-02.jpg",
      "/images/sunmoon-08.jpg",
      "/images/sunmoon-04.jpg",
      "/images/sunmoon-05.jpg",
      "/images/sunmoon-06.jpg",
      "/images/sunmoon-07.jpg",
      "/images/sunmoon-09.jpg",
      "/images/sunmoon-10.jpg",
      "/images/sunmoon-11.jpg",
    ],
  },
  {
    slug: "woman-who-ate-gold",
    index: "Recipe 02",
    title: "Woman Who Ate Gold",
    year: "2024",
    medium: "Conceptual Shoot",
    concept:
      "A woman consumed by gold. A study of beauty, glamour and femininity rendered through bold styling, rich texture and dramatic light — intense, unreal, cinematic, drenched in gold.",
    statement:
      "I do not wear the gold; I have swallowed it. Opulence becomes appetite. The shoot lingers on the threshold where adornment turns into hunger, and beauty begins to weigh on the body that carries it.",
    process: [
      "A palette built almost entirely from warm metal — skin, fabric and ornament dissolving into one another.",
      "Hands, coins and jewellery photographed close, as if mid-ritual.",
      "Dramatic, theatrical light to keep the image on the edge of the unreal.",
    ],
    credits: [
      { role: "Concept, Creative Direction, Styling & Modeling", name: "Pavitra Mishra" },
      {
        role: "Jewellery",
        name: "Aarem Jewellery",
        note: "A homegrown jewellery label run by a mother–daughter duo.",
      },
      { role: "Photography & Cinematography", name: "Aanchal Chawla, Sapan Shah" },
      { role: "Hair & Makeup", name: "Ria Panchal" },
      { role: "Studio", name: "Scenes BNB, Ahmedabad" },
    ],
    type: "image",
    cover: "/images/gold-04.jpg",
    gallery: [
      "/images/gold-02.jpg",
      "/images/gold-01.jpg",
      "/images/gold-03.jpg",
      "/images/gold-05.jpg",
      "/images/gold-06.jpg",
      "/images/gold-07.jpg",
      "/images/gold-08.jpg",
    ],
  },
  {
    slug: "after-the-credits",
    index: "Recipe 03",
    title: "After The Credits",
    year: "2024",
    medium: "Experimental Film",
    concept:
      "An experimental romance. A woman lingers in an empty theatre, in love with a film that has already ended — waiting for it to speak again, like an unsaid goodbye.",
    statement:
      "The lights have come up. Everyone has left. I stay, holding on to a story that no longer needs me. A one-sided love affair with cinema itself — tender, foolish, and impossible to walk out of.",
    process: [
      "A film built almost entirely from waiting — long stillnesses where nothing and everything happens.",
      "Sound and silence treated as characters, the empty theatre breathing around me.",
      "An ending that refuses to end, looping back toward the screen.",
    ],
    credits: [
      { role: "Concept, Creative Direction, Cinematography & Post-Production", name: "Aanchal Chawla" },
      { role: "Acting", name: "Pavitra Mishra" },
    ],
    type: "film",
    cover: "/images/aftercredits-still.jpg",
    videoId: "ySZmGeTpsoc",
    gallery: [],
  },
  {
    slug: "a-love-letter-to-sarees",
    index: "Recipe 04",
    title: "A Love Letter To Sarees",
    year: "2023",
    medium: "Editorial Shoot",
    concept:
      "A love letter to the saree — its timelessness, intimacy and grace. Captured in a heritage home in Ahmedabad: nostalgic, lived-in and deeply personal.",
    statement:
      "Six yards of memory. The saree is photographed not as costume but as inheritance — the way it falls, the way it is folded, the way it holds the women who came before. Warm frames, soft afternoons, and emotion left intentionally undisturbed.",
    process: [
      "Shot inside a heritage home in Ahmedabad to let the architecture hold the mood.",
      "Natural, warm light favoured over studio precision.",
      "Compositions kept lived-in and unhurried — a private afternoon rather than a campaign.",
    ],
    credits: [
      { role: "Concept, Creative Direction, Styling & Modeling", name: "Pavitra Mishra" },
      { role: "Saree", name: "Suta", note: "The very one I wore for my graduation." },
      { role: "Photography & Cinematography", name: "Aanchal Chawla, Sapan Shah" },
      { role: "Location", name: "A heritage home in the old city, Ahmedabad" },
    ],
    type: "image",
    cover: "/images/saree-03.jpg",
    gallery: [
      "/images/saree-01.jpg",
      "/images/saree-02.jpg",
      "/images/saree-04.jpg",
      "/images/saree-05.jpg",
      "/images/saree-06.jpg",
      "/images/saree-07.jpg",
      "/images/saree-08.jpg",
      "/images/saree-09.jpg",
      "/images/saree-10.jpg",
      "/images/saree-11.jpg",
    ],
  },
];

export const getProject = (slug) => PROJECTS.find((p) => p.slug === slug);

export const CONTACT = {
  email: "work.pavitramishra@gmail.com",
  instagramHandle: "@pavitraamishraa",
  instagramUrl: "https://instagram.com/pavitraamishraa",
};
